import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { stravaApi, StravaAnalysis } from "@/api/strava";
import { AnimatedProgress } from "@/lib/animations";

// Map personality types to emojis
const PERSONALITY_EMOJIS: Record<string, string> = {
  "The Machine": "🤖",
  "Weekend Warrior": "⚔️",
  "Sunrise Chaser": "🌅",
  "Night Owl Runner": "🦉",
  "Distance Hunter": "🎯",
  "Speed Demon": "⚡️",
  "Steady Climber": "🏔️",
  "Comeback Kid": "💪",
  "The Urban Explorer": "🚀",
};

export function AnalysisReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState<StravaAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for mock data in location state (dev mode)
  const mockData = (location.state as { mockData?: StravaAnalysis })?.mockData;

  // Fetch analysis on mount (or use mock data)
  useEffect(() => {
    // If mock data provided, use it directly
    if (mockData) {
      setAnalysis(mockData);
      setIsLoading(false);
      return;
    }

    const fetchAnalysis = async () => {
      try {
        const { analysis } = await stravaApi.getAnalysis();
        if (analysis) {
          setAnalysis(analysis);
        } else {
          navigate("/profile-success", { replace: true });
        }
      } catch (error) {
        console.error("Failed to fetch analysis:", error);
        navigate("/profile-success", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [navigate, mockData]);

  const handleBack = () => {
    // Preserve mock data when going back if in dev mode
    if (mockData) {
      navigate("/analysis-summary", { state: { mockData } });
    } else {
      navigate("/analysis-summary");
    }
  };

  const handleAcceptChallenge = () => {
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050a05] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const emoji = PERSONALITY_EMOJIS[analysis.personalityType] || "🏃";

  // Calculate period in days
  const periodStart = new Date(analysis.periodStart);
  const periodEnd = new Date(analysis.periodEnd);
  const daysAnalyzed = Math.ceil(
    (periodEnd.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000)
  );

  return (
    <div className="min-h-screen bg-[#050a05] text-white pb-32">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 flex items-center justify-between bg-[#050a05]/80 backdrop-blur-md p-4 border-b border-white/5">
        <button
          onClick={handleBack}
          className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-white/10 transition-colors border border-white/5"
        >
          <span className="material-symbols-outlined text-[20px]">
            arrow_back
          </span>
        </button>
        <h2 className="text-white text-sm font-semibold uppercase tracking-widest opacity-80">
          Full Report
        </h2>
        {/* Spacer to balance header */}
        <div className="size-10 shrink-0" />
      </header>

      <main className="flex flex-col gap-6 px-5 pt-4 max-w-md mx-auto">
        {/* Personality Section */}
        <section className="relative flex flex-col items-center justify-center rounded-[2.5rem] bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-8 pt-10 pb-10 shadow-xl overflow-hidden text-center border border-white/5 group">
          {/* Hero glow */}
          <div
            className="absolute inset-0 opacity-60 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(13, 242, 13, 0.12) 0%, rgba(5, 10, 5, 0) 70%)",
            }}
          />

          <div className="relative z-10 flex flex-col items-center">
            {/* Emoji with glow */}
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-primary blur-[40px] opacity-20" />
              <div className="text-[90px] leading-none transform group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500 drop-shadow-lg">
                {emoji}
              </div>
            </div>

            <div className="space-y-3">
              {/* Runner Type Badge */}
              <div className="inline-block px-4 py-1 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-sm mb-2">
                <span className="text-primary text-xs font-bold uppercase tracking-widest">
                  Runner Type
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl font-black tracking-tight text-white mb-2 leading-none">
                {analysis.personalityType.split(" ").slice(0, -1).join(" ")}{" "}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#a3c2a3]">
                  {analysis.personalityType.split(" ").slice(-1)}
                </span>
              </h1>

              {/* Description */}
              <p className="text-[#a3c2a3] text-base font-light leading-relaxed max-w-xs mx-auto opacity-90">
                {analysis.personalityDesc}
              </p>
            </div>
          </div>
        </section>

        {/* Performance Section */}
        <section>
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="text-white text-lg font-bold">Performance</h3>
            <span className="text-xs text-[#a3c2a3] bg-white/5 px-2 py-1 rounded-md">
              Last {daysAnalyzed} Days
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Distance */}
            <div className="group relative flex flex-col justify-between rounded-3xl bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-5 border border-white/5 hover:border-primary/30 transition-all duration-300">
              <div className="flex justify-between items-start mb-3">
                <div className="size-10 flex items-center justify-center rounded-full bg-white/5 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-[22px] text-[#a3c2a3] group-hover:text-primary">
                    straighten
                  </span>
                </div>
              </div>
              <div>
                <p className="text-[#a3c2a3] text-xs font-medium uppercase tracking-wider mb-1">
                  Distance
                </p>
                <p className="text-white text-3xl font-bold tracking-tighter">
                  {Math.round(analysis.totalDistance)}{" "}
                  <span className="text-base font-normal text-[#a3c2a3]">
                    km
                  </span>
                </p>
              </div>
            </div>

            {/* Total Runs */}
            <div className="group relative flex flex-col justify-between rounded-3xl bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-5 border border-white/5 hover:border-primary/30 transition-all duration-300">
              <div className="flex justify-between items-start mb-3">
                <div className="size-10 flex items-center justify-center rounded-full bg-white/5 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-[22px] text-[#a3c2a3] group-hover:text-primary">
                    directions_run
                  </span>
                </div>
              </div>
              <div>
                <p className="text-[#a3c2a3] text-xs font-medium uppercase tracking-wider mb-1">
                  Total Runs
                </p>
                <p className="text-white text-3xl font-bold tracking-tighter">
                  {analysis.totalActivities}
                </p>
              </div>
            </div>

            {/* Avg/Week */}
            <div className="group relative flex flex-col justify-between rounded-3xl bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-5 border border-white/5 hover:border-primary/30 transition-all duration-300">
              <div className="flex justify-between items-start mb-3">
                <div className="size-10 flex items-center justify-center rounded-full bg-white/5 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-[22px] text-[#a3c2a3] group-hover:text-primary">
                    calendar_month
                  </span>
                </div>
              </div>
              <div>
                <p className="text-[#a3c2a3] text-xs font-medium uppercase tracking-wider mb-1">
                  Avg/Week
                </p>
                <p className="text-white text-3xl font-bold tracking-tighter">
                  {analysis.avgWeeklyRuns.toFixed(1)}
                </p>
              </div>
            </div>

            {/* Streak - Highlighted */}
            <div className="group relative flex flex-col justify-between rounded-3xl bg-gradient-to-br from-[#1a261a] to-green-950/30 p-5 border border-primary/20 hover:border-primary/50 transition-all duration-300 overflow-hidden">
              {/* Corner glow */}
              <div className="absolute -right-6 -top-6 bg-primary/10 w-24 h-24 rounded-full blur-xl group-hover:bg-primary/20 transition-colors" />

              <div className="flex justify-between items-start mb-3 relative z-10">
                <div className="size-10 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-[22px]">
                    local_fire_department
                  </span>
                </div>
              </div>
              <div className="relative z-10">
                <p className="text-primary text-xs font-bold uppercase tracking-wider mb-1">
                  Streak 🔥
                </p>
                <p className="text-white text-3xl font-bold tracking-tighter">
                  {analysis.longestStreak}{" "}
                  <span className="text-base font-normal text-[#a3c2a3]">
                    days
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Match Section */}
        <section className="rounded-[2rem] bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-6 border border-white/5 relative overflow-hidden">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white text-xl font-bold">
                  Challenge Match
                </h3>
                <p className="text-[#a3c2a3] text-xs mt-1">
                  Based on your recent form
                </p>
              </div>
              {/* Tier Badge */}
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-900/40 to-yellow-600/10 px-3 py-1.5 rounded-3xl border border-yellow-500/20 shadow-sm">
                <span className="material-symbols-outlined text-[15px] text-yellow-400">
                  military_tech
                </span>
                <span className="text-[8px] font-bold text-yellow-100 tracking-widest uppercase">
                  {analysis.recommendedTier}
                </span>
              </div>
            </div>

            {/* Daily Goal */}
            <div className="flex items-baseline gap-2 mt-2">
              <h2 className="text-6xl font-black text-white tracking-tighter drop-shadow-lg">
                {analysis.recommendedGoal}
                <span className="text-4xl text-primary">k</span>
              </h2>
              <p className="text-[#a3c2a3] text-lg font-medium">daily goal</p>
            </div>

            {/* Success Prediction */}
            <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
              <div className="flex justify-between items-end mb-3">
                <p className="text-white text-sm font-medium flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[18px]">
                    trending_up
                  </span>
                  Success Prediction
                </p>
                <p className="text-primary text-lg font-bold">
                  {analysis.successPrediction}%
                </p>
              </div>
              <AnimatedProgress
                value={analysis.successPrediction}
                className="h-full rounded-full bg-gradient-to-r from-[#0ab80a] via-primary to-white"
                trackClassName="h-3 w-full rounded-full bg-white/5"
                delay={0.3}
              />
            </div>

            {/* Why Section */}
            {analysis.matchReasoning && (
              <div className="relative bg-white/5 rounded-xl p-4 border-l-2 border-primary">
                <p className="text-gray-300 text-sm leading-relaxed">
                  <span className="text-primary font-bold mr-1">Why?</span>
                  {analysis.matchReasoning}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Insight Section */}
        {analysis.funFactStat && analysis.funFactMessage && (
          <section className="rounded-[2rem] bg-gradient-to-r from-[#111] to-[#162216] p-6 border border-white/10 relative overflow-hidden group">
            {/* Corner glow */}
            <div className="absolute -right-10 -bottom-10 bg-primary/5 w-40 h-40 rounded-full blur-2xl group-hover:bg-primary/10 transition-all" />

            <div className="flex flex-col gap-4 relative z-10">
              <div className="flex items-center gap-2 text-primary/80 mb-1">
                <span className="material-symbols-outlined text-[20px]">
                  lightbulb
                </span>
                <span className="text-xs font-bold uppercase tracking-widest">
                  Insight
                </span>
              </div>

              <div className="text-5xl font-black text-white tracking-tighter">
                {analysis.funFactStat}
              </div>

              <p className="text-gray-300 text-sm leading-relaxed border-t border-white/10 pt-4 mt-1">
                {analysis.funFactMessage}
              </p>
            </div>
          </section>
        )}

        {/* Quote Section */}
        {analysis.motivation && (
          <section className="rounded-3xl bg-gradient-to-br from-[#1a261a] to-[#0d120d] p-6 border border-white/5 relative">
            <span className="material-symbols-outlined text-[#a3c2a3]/20 text-[40px] absolute top-4 left-4 rotate-180">
              format_quote
            </span>
            <div className="pt-8 text-center px-4">
              <p className="text-white text-lg italic font-light leading-relaxed text-gray-200">
                "{analysis.motivation}"
              </p>
            </div>
          </section>
        )}
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 w-full px-5 pb-6 pt-8 bg-gradient-to-t from-[#050a05] via-[#050a05]/95 to-transparent z-40 pointer-events-none flex justify-center">
        <button
          onClick={handleAcceptChallenge}
          className="pointer-events-auto w-full max-w-md bg-primary text-black font-bold text-base py-4 rounded-full shadow-[0_0_20px_rgba(13,242,13,0.4)] hover:shadow-[0_0_30px_rgba(13,242,13,0.6)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 group"
        >
          <span className="tracking-wide">Create or Join Challenge</span>
          <span className="material-symbols-outlined text-[18px] group-hover:translate-x-0.5 transition-transform">
            arrow_forward
          </span>
        </button>
      </div>
    </div>
  );
}
